import { useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { useLists } from '@/hooks/use-lists'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { cn } from '@/lib/utils'
import { hasFieldError, useFieldContext } from './shared'
import { NativeSelectFieldShell } from './native-select-shell'

type Option = { value: number; label: string }

type FieldProps = {
  label: string
  className?: string
  placeholder?: string
  optional?: boolean
  orientation?: 'vertical' | 'horizontal'
}

/** The sector: 147 options plus "Sector not specified". */
export function SectorField(props: FieldProps) {
  const { t } = useLingui()
  const lists = useLists()
  return <ListSelectField {...props} placeholder={props.placeholder ?? t`Select a sector`} items={lists.sector} />
}

/** The job function: 34 options plus "Function not defined". */
export function FunctionField(props: FieldProps) {
  const { t } = useLingui()
  const lists = useLists()
  return <ListSelectField {...props} placeholder={props.placeholder ?? t`Select a function`} items={lists.function} />
}

/**
 * A long numbered list as a searchable Combobox, one flat list sorted by label in the viewer's language. The last
 * item of `items` is the "not specified" entry, which closes the list. The stored value is the item's index.
 */
function ListSelectField(props: FieldProps & { placeholder: string; items: string[] }) {
  const { t, i18n } = useLingui()
  const field = useFieldContext<number | null | undefined>()
  const [search, setSearch] = useState('')
  const collator = new Intl.Collator(i18n.locale)
  const notSpecifiedValue = props.items.length - 1
  const options: Option[] = props.items
    .slice(0, notSpecifiedValue)
    .map((label, value) => ({ value, label }))
    .sort((a, b) => collator.compare(a.label, b.label))
  const notSpecified: Option = { value: notSpecifiedValue, label: props.items[notSpecifiedValue] ?? '' }
  const allOptions = [...options, notSpecified]
  const query = search.trim().toLocaleLowerCase(i18n.locale)
  const matches = (option: Option) => option.label.toLocaleLowerCase(i18n.locale).includes(query)
  const shownOptions = options.filter(matches)
  const showNotSpecified = matches(notSpecified)
  const value = field.state.value
  const selected = allOptions.find((option) => option.value === value) ?? null

  return (
    <NativeSelectFieldShell
      label={props.label}
      className={props.className}
      optional={props.optional}
      orientation={props.orientation}
      readOnlyLabel={selected?.label}
    >
      <Combobox
        items={allOptions}
        value={selected}
        onValueChange={(item: Option | null) => {
          if (item) field.handleChange(item.value)
        }}
        onInputValueChange={setSearch}
        isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
        // The shown set, so base-ui sees an empty list and "No results found" appears when nothing matches.
        filter={(item: Option) => matches(item)}
      >
        <ComboboxTrigger
          render={
            <Button
              id={field.name}
              type="button"
              variant="outline"
              aria-invalid={hasFieldError(field.state.meta) || undefined}
              onBlur={field.handleBlur}
              className={cn('h-8 w-full justify-between font-normal', !selected && 'text-muted-foreground')}
            />
          }
        >
          <span className="truncate">{selected ? selected.label : props.placeholder}</span>
        </ComboboxTrigger>
        <ComboboxContent className="min-w-(--anchor-width)">
          <ComboboxInput showTrigger={false} placeholder={t`Search...`} />
          <ComboboxList>
            {shownOptions.map((option) => (
              <ComboboxItem key={option.value} value={option}>
                {option.label}
              </ComboboxItem>
            ))}
            {showNotSpecified && (
              <>
                {shownOptions.length > 0 && <ComboboxSeparator />}
                <ComboboxItem value={notSpecified}>{notSpecified.label}</ComboboxItem>
              </>
            )}
          </ComboboxList>
          <ComboboxEmpty>{t`No results found`}</ComboboxEmpty>
        </ComboboxContent>
      </Combobox>
    </NativeSelectFieldShell>
  )
}
